import React, { useState, useEffect, useRef } from 'react';
import { soundManager } from '../../utils/soundManager';
import './Testimonials.css';

const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [videosPlaying, setVideosPlaying] = useState({}); // for sound toggle state
  const [videoStatus, setVideoStatus] = useState({}); // debug status per video

  const sectionRef = useRef(null);
  const videoRefs = useRef({});
  const autoPlayRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      name: 'James Chen',
      title: 'Watch Collector',
      location: 'Singapore',
      videoUrl: '/videos/client-james-chen.mp4',
      quote: 'Unparalleled craftsmanship. The tourbillon movement is a work of art. This timepiece represents the pinnacle of horological excellence.',
      collection: 'Royal Tourbillon'
    },
    {
      id: 2,
      name: 'Isabella Rossi',
      title: 'CEO, Rossi Group',
      location: 'Milan, Italy',
      videoUrl: '/videos/client-isabella-rossi.mp4',
      quote: 'CHRONOS combines traditional Swiss watchmaking with modern elegance. The attention to detail is extraordinary. A true heirloom piece.',
      collection: 'Classic Series'
    },
    {
      id: 3,
      name: 'Michael Torres',
      title: 'Investment Banker',
      location: 'New York, USA',
      videoUrl: '/videos/client-michael-torres.mp4',
      quote: 'The precision and reliability are remarkable. After owning multiple luxury watches, this is the one I wear every day. Worth every penny.',
      collection: 'Sport Chronograph'
    },
    {
      id: 4,
      name: 'Sophie Laurent',
      title: 'Art Director',
      location: 'Paris, France',
      videoUrl: '/videos/client-sophie-laurent.mp4',
      quote: 'A masterpiece on the wrist. The rose gold case and intricate dial design showcase true artisanship. Absolutely breathtaking.',
      collection: 'Royal Series'
    },
    {
      id: 5,
      name: 'David Müller',
      title: 'Entrepreneur',
      location: 'Geneva, Switzerland',
      videoUrl: '/videos/client-david-muller.mp4',
      quote: 'Being Swiss, I appreciate fine watchmaking. CHRONOS exceeded my expectations. The heritage and quality are evident in every detail.',
      collection: 'Classic Perpetual'
    }
  ];

  // Intersection Observer — trigger when section enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  // Auto-play every 10 seconds when visible + enabled
  useEffect(() => {
    if (!isAutoPlaying || !isVisible) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      return;
    }

    autoPlayRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % testimonials.length);
    }, 10000);

    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, isVisible, testimonials.length]);

  // CRITICAL: Play only active video + wait for it to be ready
  useEffect(() => {
    // Only attempt to play when the section is visible (avoids wasted network requests and autoplay race conditions)
    if (!isVisible) {
      // Pause and reset all videos
      Object.keys(videoRefs.current).forEach(key => {
        const v = videoRefs.current[key];
        if (v) {
          try { v.pause(); v.currentTime = 0; } catch (e) {}
        }
      });
      return;
    }

    const video = videoRefs.current[activeIndex];
    if (!video) return;

    // Force correct settings
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    video.currentTime = 0;

    let clickHandler = null;
    let attemptCancel = null;
    const attemptPlay = (vid) => {
      if (!vid) return Promise.resolve();
      try {
        vid.muted = true;
        vid.playsInline = true;
        vid.preload = 'auto';
      } catch (e) {}

      if (vid.readyState >= 3) {
        return vid.play();
      }

      let cancelFns = [];
      const p = new Promise((resolve) => {
        const onCanPlay = () => {
          try { vid.removeEventListener('canplay', onCanPlay); } catch (e) {}
          vid.play().then(resolve).catch((err) => {
            console.warn('Video autoplay failed after canplay:', err);
            resolve();
          });
        };
        vid.addEventListener('canplay', onCanPlay, { once: true });
        cancelFns.push(() => { try { vid.removeEventListener('canplay', onCanPlay); } catch(e){} });
        try { vid.load(); } catch (e) {}
        // fallback timeout
        const timeout = setTimeout(() => {
          vid.play().then(resolve).catch((err) => {
            console.warn('Video autoplay attempt timed out / failed:', err);
            resolve();
          });
        }, 800);
        cancelFns.push(() => clearTimeout(timeout));
      });
      attemptCancel = () => cancelFns.forEach(fn => fn());
      return p.catch((err) => { console.warn('attemptPlayErr', err); });
    };

    // Try to play the current video, and add a click handler to the container if autoplay was blocked
    attemptPlay(video).then(() => {
      if (video && video.paused) {
        clickHandler = () => {
          try { video.play(); } catch (e) { console.warn('Play after click failed:', e); }
          if (sectionRef.current) sectionRef.current.removeEventListener('click', clickHandler);
        };
        if (sectionRef.current) sectionRef.current.addEventListener('click', clickHandler, { once: true });
      }
    }).catch((e) => console.warn('attemptPlay error', e));

    // Pause all other videos
    Object.keys(videoRefs.current).forEach(key => {
      const idx = Number(key);
      if (idx !== activeIndex && videoRefs.current[idx]) {
        videoRefs.current[idx].pause();
        videoRefs.current[idx].currentTime = 0;
      }
    });

    return () => {
      try { if (attemptCancel) attemptCancel(); } catch (e) {}
      if (clickHandler && sectionRef.current) {
        sectionRef.current.removeEventListener('click', clickHandler);
      }
    };
  }, [activeIndex, isVisible]);

  // Unlock autoplay unlock on first user gesture (iOS/Safari nuclear fix)
  useEffect(() => {
    const unlock = () => {
      Object.values(videoRefs.current).forEach(v => {
        if (v) {
          v.muted = true;
          v.play().catch(() => {});
        }
      });
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('click', unlock);
    };

    document.addEventListener('touchstart', unlock);
    document.addEventListener('click', unlock);

    return () => {
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('click', unlock);
    };
  }, []);

  const handlePrevious = () => {
    soundManager.play('ui-click', 0.3);
    setIsAutoPlaying(false);
    setActiveIndex(prev => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const handleNext = () => {
    soundManager.play('ui-click', 0.3);
    setIsAutoPlaying(false);
    setActiveIndex(prev => (prev + 1) % testimonials.length);
  };

  const handleDotClick = (index) => {
    soundManager.play('ui-click', 0.3);
    setIsAutoPlaying(false);
    setActiveIndex(index);
  };

  const handleResumeAutoPlay = () => setIsAutoPlaying(true);

  const toggleVideoSound = (index) => {
    const video = videoRefs.current[index];
    if (video) {
      video.muted = !video.muted;
      const nowPlaying = !video.muted && !video.paused;
      setVideosPlaying(prev => ({ ...prev, [index]: nowPlaying }));
      // If user unmuted but the video is paused, attempt to resume playback
      if (!video.muted && video.paused) {
        video.play().catch(err => console.warn('Play failed after unmuting:', err));
      }
    }
  };

  const togglePlayback = (index) => {
    const v = videoRefs.current[index];
    if (!v) return;
    if (v.paused) {
      v.play().then(() => {
        setVideosPlaying(prev => ({ ...prev, [index]: true }));
      }).catch(err => console.warn('Play failed:', err));
    } else {
      v.pause();
      setVideosPlaying(prev => ({ ...prev, [index]: false }));
    }
  };

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className={`testimonials-section ${isVisible ? 'visible' : ''}`}
    >
      <div className="testimonials-container">

        {/* Header */}
        <div className="testimonials-header">
          <div className="header-label">
            <span className="label-line"></span>
            <span className="label-text">Client Stories</span>
            <span className="label-line"></span>
          </div>
          <h2 className="testimonials-title">What Collectors Say</h2>
          <p className="testimonials-subtitle">
            Trusted by discerning collectors and connoisseurs worldwide.
            Each timepiece tells a story of excellence.
          </p>
        </div>

        {/* Slider */}
        <div className="testimonials-slider">

          <div className="testimonials-track">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`testimonial-card ${
                  index === activeIndex ? 'active' : ''
                }`}
              >
                {/* Video Background */}
                <div className="testimonial-video-container">
                  <video
                    ref={el => {
                      if (el) {
                        videoRefs.current[index] = el;
                        // make sure webkit attributes are present for iOS
                        try {
                          el.setAttribute('playsinline', 'true');
                          el.setAttribute('webkit-playsinline', 'true');
                          el.setAttribute('x-webkit-airplay', 'allow');
                        } catch (e) {}
                        // track play state
                        el.onplay = () => {
                          setVideosPlaying(prev => ({ ...prev, [index]: true }));
                          setVideoStatus(prev => ({ ...prev, [index]: { ...prev[index], paused: false } }));
                        };
                        el.onpause = () => {
                          setVideosPlaying(prev => ({ ...prev, [index]: false }));
                          setVideoStatus(prev => ({ ...prev, [index]: { ...prev[index], paused: true } }));
                        };
                        el.onended = () => {
                          setVideosPlaying(prev => ({ ...prev, [index]: false }));
                          setVideoStatus(prev => ({ ...prev, [index]: { ...prev[index], ended: true } }));
                        };
                        el.oncanplay = () => setVideoStatus(prev => ({ ...prev, [index]: { ...(prev[index] || {}), readyState: el.readyState } }));
                        el.onerror = () => setVideoStatus(prev => ({ ...prev, [index]: { ...(prev[index] || {}), error: true } }));
                      } else if (videoRefs.current[index]) {
                        delete videoRefs.current[index];
                      }
                    }}
                    className="testimonial-video"
                    loop
                    muted
                    playsInline
                    preload="auto"
                    poster={`/images/testimonials/poster-${testimonial.id}.jpg`}
                    // key={testimonial.videoUrl} // avoids unnecessary remounts; keep stable in parent map
                  >
                    <source src={testimonial.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>

                  <div className="video-overlay"></div>

                  {/* Play overlay when autoplay blocked */}
                  {index === activeIndex && (
                    <button
                      className="video-play-overlay"
                      onClick={() => togglePlayback(index)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlayback(index); } }}
                      aria-label={videosPlaying[index] ? 'Pause testimonial video' : 'Play testimonial video'}
                      tabIndex={0}
                    >
                      {videosPlaying[index] ? (
                        <svg viewBox="0 0 24 24" fill="none"><path d="M6 4h4v16H6zM14 4h4v16h-4z" fill="currentColor"/></svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none"><path d="M5 3v18l15-9L5 3z" fill="currentColor"/></svg>
                      )}
                    </button>
                  )}

                  {/* Sound Toggle (only on active slide) */}
                  {index === activeIndex && (
                    <button
                      className="sound-toggle"
                      onClick={() => toggleVideoSound(index)}
                      aria-label={videosPlaying[index] ? 'Mute' : 'Unmute'}
                    >
                      {videosPlaying[index] ? (
                        <svg viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
                      ) : (
                        <svg viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
                      )}
                    </button>
                  )}
                </div> {/* close testimonial-video-container */}
                <div className="testimonial-content-overlay">
                  <blockquote className="testimonial-quote">“{testimonial.quote}”</blockquote>

                  <div className="collection-badge">
                    <span className="badge-icon">◆</span>
                    <span className="badge-text">{testimonial.collection}</span>
                  </div>

                  <div className="testimonial-author">
                    <div className="author-info">
                      <h4 className="author-name">{testimonial.name}</h4>
                      <p className="author-title">{testimonial.title}</p>
                      <p className="author-location">{testimonial.location}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Nav Buttons */}
          <button className="nav-button prev-button" onClick={handlePrevious} aria-label="Previous">
            <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
          </button>
          <button className="nav-button next-button" onClick={handleNext} aria-label="Next">
            <svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
          </button>
        </div>

        {/* Dots */}
        <div className="testimonials-pagination">
          {testimonials.map((_, i) => (
            <button
              key={i}
              className={`pagination-dot ${i === activeIndex ? 'active' : ''}`}
              onClick={() => handleDotClick(i)}
              aria-label={`Testimonial ${i + 1}`}
            >
              <span className="dot-inner"></span>
            </button>
          ))}
        </div>

        {/* Resume autoplay button */}
        {!isAutoPlaying && (
          <button className="autoplay-control" onClick={handleResumeAutoPlay}>
            <span className="control-icon">Play</span>
            <span className="control-text">Resume Auto-play</span>
          </button>
        )}

        {/* Stats */}
        <div className="testimonials-stats">
          <div className="stat-item">
            <span className="stat-icon">★</span>
            <div className="stat-content">
              <span className="stat-number">5.0</span>
              <span className="stat-label">Average Rating</span>
            </div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-icon">◆</span>
            <div className="stat-content">
              <span className="stat-number">2,500+</span>
              <span className="stat-label">Satisfied Collectors</span>
            </div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-icon">◈</span>
            <div className="stat-content">
              <span className="stat-number">98%</span>
              <span className="stat-label">Recommend CHRONOS</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;